const _ = require('lodash');
const moment = require('moment');
const Papa = require('papaparse');
const queryString = require('query-string');
const { default: Constants } = require('./constants');

// If length=0, means no limit to length of acronym
// E.g. { word="Marina Square", length=2} => "MS"
// E.g. { word="Marina Square", length=1} => "M"
// However, if word consists of only 1 token,
// then we return the first two chars of the token
// E.g. { word="Starbucks" } => "ST"
const AcronymWord = (phrase, length = 0) => {
  const words = phrase.split(' ');
  let acronym = '';

  if (length === 0) {
    return phrase;
  }
  if (words.length === 1) {
    return length < phrase.length ? phrase.substring(0, length).toUpperCase() : phrase;
  }

  for (let i = 0; i < (words.length >= length ? length : words.length); i += 1) {
    acronym += words[i].charAt(0).toUpperCase();
  }

  let wordIndex = 0;
  let letterIndex = 1;
  while (acronym.length < length) {
    if (!words[wordIndex].charAt(letterIndex)) {
      if ((wordIndex + 1) >= words.length && (letterIndex + 1) >= words[wordIndex].length) {
        return acronym;
      }
      wordIndex += 1;
      if (wordIndex === words.length) {
        letterIndex += 1;
        wordIndex = 0;
      }
      continue;
    }
    const letter = words[wordIndex].charAt(letterIndex).toUpperCase();
    const letterPos = (wordIndex + 1) * (letterIndex + 1) - 1;
    const acronymArr = acronym.split('');
    acronymArr.splice(letterPos, 0, letter);
    acronym = acronymArr.join('');

    if ((wordIndex + 1) >= words.length && (letterIndex + 1) >= words[wordIndex].length) {
      return acronym;
    }
    wordIndex += 1;
    if (wordIndex === words.length) {
      letterIndex += 1;
      wordIndex = 0;
    }
  }
  return acronym;
};

const ConvertDateToSGTime = (date) => {
  if (!(date instanceof Date)) {
    throw new Error('ConvertDateToSGTime(): input date is not a Date type');
  }
  return new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
};

const IsEmpty = (obj) => {
  if (obj === null || obj === undefined || typeof obj === 'undefined' || obj === '' || obj === 'undefined' || obj === 'null') {
    return true;
  } else if (Array.isArray(obj) && obj.length === 0) {
    return true;
  } else {
    return false;
  }
};

const ConvertStartDate = (date) => {
  if (IsEmpty(date)) {
    throw new Error('ConvertStartDate(): input date is required');
  }

  return (new Date(date)).toISOString().replace('T', ' ').replace('Z', '+08');
};

const ConvertEndDate = (date) => {
  if (IsEmpty(date)) {
    throw new Error('ConvertEndDate(): input date is required');
  }

  return (new Date(date)).toISOString().replace('T', ' ').replace('00:00:00', '23:59:59')
    .replace('Z', '+08');
};

const AcraFormatCheck = (num) => {
  const pattern = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

  if (!IsEmpty(num)) {
    const lastChar = num.charAt(num.length - 1);
    if (num.length >= 9 && num.length <= 10 && isNaN(lastChar.toString()) && !pattern.test(num)) {
      return true;
    }
  }

  return false;
};

const convertDataURLtoBlob = (dataurl) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

const getFileDataUrl = async (file, onError = () => { }) => {
  try {
    const reader = new FileReader();
    return await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => {
        const error = new Error('Error reading file');
        onError(error);
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  } catch (err) {
    onError(err);
  }
};

const formatDatetoString = (date) => {
  if (!IsEmpty(date)) {
    return moment(date).format('DD MMMM YYYY');
  }
};

const showDateInSGTime = (date) => (date ? moment(date).utcOffset(+8, false).format('YYYY-MM-DD') : null);

const formatDateWithTimetoString = (date, noTime) => {
  if (!IsEmpty(date)) {
    if (noTime) return moment(date).format('DD/MM/YYYY');
    return moment(date).format('DD/MM/YYYY, h:mm:ss a');
  }
};

const formatCardDateToString = (date) => {
  if (!IsEmpty(date)) {
    return moment(date).format('MM/YY');
  }
};

const getThumbnailFromVideoURL = (videoUrl) => videoUrl.replace(`${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.mp4`, `thumbnails/${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.jpg`);

const reduceFuncForTotal = ({ arr, item, initial = 0 }) => {
  if (item && arr) {
    return arr.reduce((total, next) => total + next.count, initial);
  } else {
    return 0;
  }
};

const datePeriodToString = (strDateStart, strDateEnd) => {
  let periodStr = '';
  if (!IsEmpty(strDateStart) && !IsEmpty(strDateEnd)) {
    const momentStart = moment(new Date(strDateStart));
    const momentEnd = moment(new Date(strDateEnd));
    if (momentStart.format('MMM') !== momentEnd.format('MMM')) {
      periodStr = `${momentStart.format('D')} ${momentStart.format('MMM')} - ${momentEnd.format('D')} ${momentEnd.format('MMM')} ${momentEnd.format('YYYY')}`;
    } else {
      periodStr = `${momentStart.format('D')} - ${momentEnd.format('D')} ${momentEnd.format('MMM')} ${momentEnd.format('YYYY')}`;
    }
  }
  return periodStr;
};

const EscapeSingleQuote = (str = '') => {
  if (!IsEmpty(str)) {
    return str.replace(/\'/g, "''");
  } else { return str; }
};

const emailValidation = (email) => {
  const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

  if (IsEmpty(email)) return 'Email is required';
  if (!email.match(regex)) return 'Email is invalid';

  return '';
};

// E.g. urlQueryStr = '?upgradePlan=true'
const ExtractParamsFromUrlQueryStr = (urlQueryStr = '') => {
  if (IsEmpty(urlQueryStr)) {
    return null;
  }
  return queryString.parse(urlQueryStr);
};

const passwordValidation = (password) => {
  if (IsEmpty(password)) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';

  return '';
};

const passwordMatchValidation = (password, matchPassword) => {
  if (IsEmpty(password) || IsEmpty(matchPassword)) return 'Password is required';
  if (password !== matchPassword) return 'Password does not match';

  return '';
};

const Sleep = (ms = 100) => new Promise((resolve) => setTimeout(resolve, ms));

const StringToLowercase = (string) => string.toLowerCase();

const Trim = (str = '', trimMiddle = false, trimMiddleToSingleSpace = false, trimSpaceNotNewLine = false) => {
  if (!IsEmpty(str)) {
    str = String(str).trim();
    // To only trim space inside string, exclude new line on trimming
    if (trimSpaceNotNewLine) {
      return str.replace(/ +?/g, '');
    }
    if (trimMiddleToSingleSpace) {
      str = str.replace(/\s\s+/g, ' ');
    }
    if (trimMiddle) {
      str = str.replace(/\s+/g, '');
    }
    return str;
  } else { return str; }
};

const ConvertToPascalCase = (string) => {
  string = Trim(string, false, true);
  if (!IsEmpty(string)) {
    return string
      .split(' ')
      .map((word) => word[0]
        .toUpperCase()
        .concat(word.slice(1).toLowerCase()))
      .join(' ');
  } else {
    return string;
  }
};

const formatDateToDayMonthNameYear = (date, hasTime = false, timeFormat = '24h') => {
  let dateFormat = 'D MMM YYYY';
  const timeFormats = {
    '24h': 'HH:mm',
    '12h': 'hh:mmA',
  };

  if (hasTime) {
    dateFormat = `${dateFormat} ${timeFormats[timeFormat]}`;
  }

  return (date ? moment(date).format(dateFormat) : null);
};

const convertTime12to24 = (time, isFrom2DigitHoursAndMins = false) => {
  if (isFrom2DigitHoursAndMins) {
    return moment(time, 'hh:mm A').format('HH:mm');
  } else {
    return moment(time, 'h:m A').format('HH:mm');
  }
};

const convertTime24to12 = (time, isReturn2DigitHoursAndMins = false) => {
  if (isReturn2DigitHoursAndMins) {
    return moment(time, 'HH:mm').format('hh:mm A');
  } else {
    return moment(time, 'HH:mm').format('h:m A');
  }
};

function convertDegreesToRadians(degrees) {
  return degrees * (Math.PI / 180);
}

function convertRadiansToDegrees(radians) {
  return radians * (180 / Math.PI);
}

function computeOffset(location, meter, degree = 0) {
  const EARTH_RADIUS = 6371009;

  const distance = meter / EARTH_RADIUS;
  const heading = convertDegreesToRadians(90 - degree);
  const fromLat = convertDegreesToRadians(location.lat);
  const fromLng = convertDegreesToRadians(location.lng);

  const cosDistance = Math.cos(distance);
  const sinDistance = Math.sin(distance);
  const sinFromLat = Math.sin(fromLat);
  const cosFromLat = Math.cos(fromLat);
  const sinLat = cosDistance * sinFromLat + sinDistance * cosFromLat * Math.cos(heading);

  const dLng = Math.atan2(sinDistance * cosFromLat * Math.sin(heading), cosDistance - sinFromLat * sinLat);
  return { lat: convertRadiansToDegrees(Math.asin(sinLat)), lng: convertRadiansToDegrees(fromLng + dLng) };
}

async function getImageSize(src) {
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image from ${src}`));
    };
    img.src = src;
  });
}

async function validateImageWidth(src, { min = null, max = null }) {
  const { width } = await getImageSize(src);
  return (
    (IsEmpty(min) || (!IsEmpty(min) && width >= min))
    && (IsEmpty(max) || (!IsEmpty(max) && width <= max))
  );
}

function isHexColour(str) {
  if (IsEmpty(str)) {
    return false;
  }
  return /^#(([0-9A-Fa-f]{2}){3,4}|[0-9A-Fa-f]{3})$/.test(str);
}

/**
 * Calculate the expiry date num of months from date in GMT+08 in 23:59:59 hours.
 * E.g. 1 month expiry from 1st Jan shall return expiry date = 31st Jan 23:59:59.
 * E.g. 0 month and 1 day represents the end of the day itself
 * NOTE: if both month & day are null or value 0, then expiry date is yesterday's end of day
 * @param {*} date
 * @param {*} numberOfMonths
 * @param {*} numberOfDays
 * @returns
 */
function CalculateExpiryDateInSGT(date, numberOfMonths, numberOfDays) {
  const kMonthsAndDaysFromDate = moment(date).add(numberOfMonths || 0, 'months').add(numberOfDays || 0, 'days').utcOffset('+08:00');
  return new Date(`${moment(kMonthsAndDaysFromDate).subtract(1, 'day').format('YYYY-MM-DD')} 23:59:59+08`);
}

/**
 * @param {*} coords should be an array of two-item arrays representing the `x` and `y` coords
 * @param {*} imageSize object with `width` and `height` property
 * @returns {string} e.g. `polygon(0% 20%, 25% 15%, ...)`
 */
function getDynamicPolygonClipPath(coords, imageSize) {
  const { width, height } = imageSize;
  const relativeCoords = [];

  for (const coord of coords) {
    const [x, y] = coord;
    relativeCoords.push(`${(x / width) * 100}% ${(y / height) * 100}%`);
  }

  return `polygon(${relativeCoords.join(',')})`;
}

const isCoordOutOfSG = (coord) => {
  if (IsEmpty(coord) || IsEmpty(coord.lat) || IsEmpty(coord.lng)) {
    return true;
  }
  return (coord.lat < 1.220782816495816 || coord.lat > 1.4679429323707653
    || coord.lng < 103.56107025042246 || coord.lng > 104.08852084727206
  );
};

/**
 * Returns null if user didn't give permission to access location
 * Else, returns {lat, lng}
 */
const getCurrentLocation = async (timeout = 3000) => {
  try {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    } else {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
          }, (error) => {
            console.log('Util.js -> getCurrentLocation() - Error getting current location: ', error);
            reject(error);
          }, {
            maximumAge: 12000,
            timeout,
          },
        );
      });
    }
  } catch (error) {
    console.log('Util.js -> getCurrentLocation(): ', error);
    throw new Error(Constants.Error.UnexpectedError);
  }
};

const hexToRgba = (hex, alpha = 1) => {
  if (IsEmpty(hex) || typeof hex !== 'string') {
    return null;
  }

  let value = hex.trim().replace(/^#/, '');

  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(value)) {
    return null;
  }

  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }

  let a = Number(alpha);
  if (!Number.isFinite(a) || a < 0 || a > 1) {
    a = 1;
  }

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Returns null if user didn't give permission to access location
 * Else, returns {lat, lng}
 */
const GetUserCurrentLocation = async (timeout = 3000) => {
  try {
    // request and get current location
    return getCurrentLocation(timeout);
  } catch (err) {
    console.log('Error GetUserCurrentLocation(): ', err);
    throw new Error(err);
  }
};

const formatTag = (obj) => String(obj).replace(/(\n)/gm, '').trim().toLowerCase();

const IsCsvFile = (file) => file instanceof File
    && file.type === 'text/csv'
    && file.name.endsWith('.csv');

const parseCsvFile = (file, hasHeaderRow = true) => new Promise((resolve, reject) => {
  if (IsEmpty(file) || !IsCsvFile(file)) {
    resolve([]);
    return;
  }
  Papa.parse(file, {
    header:         false,
    skipEmptyLines: true,
    worker:         true,
    complete:       (results) => {
      try {
        const rows = results.data;
        if (IsEmpty(rows)) {
          resolve([]);
          return;
        }

        let headerRow;
        let dataRows;

        if (hasHeaderRow) {
          [headerRow, ...dataRows] = rows;
        } else {
          headerRow = _.map(rows[0], (_cell, i) => String(i));
          dataRows = rows;
        }

        const keys = _.map(headerRow, (cell, colIndex) => {
          const formattedCell = Trim(cell, false, false, false);
          return (!IsEmpty(formattedCell) ? formattedCell : String(colIndex));
        });

        const parsedData = _.chain(dataRows)
          .filter((row) => !_.every(row, (cell) => IsEmpty(cell)))
          .map((row) => _.zipObject(
            keys,
            _.map(row, (cell) => (IsEmpty(cell) ? '' : cell)),
          ))
          .value();

        resolve(parsedData);
      } catch (err) {
        reject(err);
      }
    },
    error: (err) => reject(err),
  });
});

module.exports = {
  AcraFormatCheck,
  AcronymWord,
  IsEmpty,
  IsCsvFile,
  CalculateExpiryDateInSGT,
  ConvertDateToSGTime,
  convertDataURLtoBlob,
  ConvertToPascalCase,
  ConvertStartDate,
  ConvertEndDate,
  formatDatetoString,
  formatDateWithTimetoString,
  formatCardDateToString,
  GetUserCurrentLocation,
  getDynamicPolygonClipPath,
  getImageSize,
  getFileDataUrl,
  hexToRgba,
  validateImageWidth,
  datePeriodToString,
  EscapeSingleQuote,
  emailValidation,
  getThumbnailFromVideoURL,
  ExtractParamsFromUrlQueryStr,
  passwordValidation,
  passwordMatchValidation,
  reduceFuncForTotal,
  Sleep,
  showDateInSGTime,
  StringToLowercase,
  Trim,
  formatDateToDayMonthNameYear,
  convertTime12to24,
  convertTime24to12,
  computeOffset,
  isHexColour,
  isCoordOutOfSG,
  formatTag,
  parseCsvFile,
};
